import {skipToken} from '@reduxjs/toolkit/query';
import {ColorSchemeType, FlagProps, Parameter} from 'platform/components';
import {useDateTimeFormatter} from 'platform/locale';

import {isNotNil} from 'ramda';

import {useGetWorkshopInterestQuery, WorkshopInterestState} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {workshopRoutes} from '@dms/routes';

import {buildArray, composePath, parseDate} from 'shared/web';

import {PairedRecordView} from './PairedRecordView';

const LOADING_PLACEHOLDER = '…';

const STATE_COLOR_SCHEME: Record<WorkshopInterestState, ColorSchemeType> = {
  NEW: 'blue',
  OPEN: 'blue',
  CLOSED: 'neutral',
  UNSUCCESSFUL: 'red',
};

export function useWorkshopInterestPairedRecord(
  workshopInterestId: string | null
): PairedRecordView {
  const {
    data: workshopInterest,
    isLoading,
    isError,
  } = useGetWorkshopInterestQuery(
    isNotNil(workshopInterestId) ? {id: workshopInterestId} : skipToken
  );

  const formatDateTime = useDateTimeFormatter();

  const code =
    workshopInterest?.publicId ??
    (isLoading ? LOADING_PLACEHOLDER : i18n.t('general.notifications.notFound'));
  const createdAtFormatted = workshopInterest?.createdAt
    ? formatDateTime('dateShort', parseDate(workshopInterest.createdAt))
    : null;
  const updatedAtFormatted = workshopInterest?.updatedAt
    ? formatDateTime('dateShort', parseDate(workshopInterest.updatedAt))
    : null;
  const firstContactAtFormatted = workshopInterest?.firstContactAt
    ? formatDateTime('dateShort', parseDate(workshopInterest.firstContactAt))
    : null;

  const parameters = buildArray<Parameter>()
    .when(isNotNil(createdAtFormatted), `${i18n.t('general.labels.created')} ${createdAtFormatted}`)
    .when(
      isNotNil(updatedAtFormatted),
      `${i18n.t('general.labels.lastUpdated')} ${updatedAtFormatted}`
    )
    .when(
      isNotNil(firstContactAtFormatted),
      `${i18n.t('page.checklistAudits.pairing.firstContact')} ${firstContactAtFormatted}`
    );

  const flags = buildArray<FlagProps>().when(isNotNil(workshopInterest), {
    label: isNotNil(workshopInterest)
      ? i18n.t(`entity.workshopInterest.states.${workshopInterest.state}`)
      : '',
    colorScheme: isNotNil(workshopInterest)
      ? STATE_COLOR_SCHEME[workshopInterest.state]
      : 'neutral',
  });

  return {
    isLoading,
    isError,
    code,
    detailPath: isNotNil(workshopInterestId)
      ? composePath(workshopRoutes.interestDetailOverview, {params: {id: workshopInterestId}})
      : null,
    parameters,
    flags,
    vehicleIds: isNotNil(workshopInterest?.vehicleId) ? [workshopInterest.vehicleId] : [],
  };
}
